import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { BookOpen, Home, Image, Sparkles, Utensils } from 'lucide-react';
import { useEffect, useMemo } from 'react';

import { appConfig } from '../config/appConfig';
import { ActiveSubjectProvider, useActiveSubject } from '../context/ActiveSubjectContext';
import { ProductStudyModeProvider, useProductStudyMode } from '../context/ProductStudyModeContext';
import { AppMenuDrawer } from './AppMenuDrawer';
import { IcpFooter } from './IcpFooter';
import { SubjectPickerButton } from './SubjectPickerButton';
import { peekPreferFactoryLogin, peekWechatFactoryReturn } from '../lib/factoryContext';
import { SUBJECT_CONFIG } from '../utils/subjectConfig';
import { isTtqSubjectEnabled } from '../services/ttqService';

const homeNavItem = { to: '/', label: '首页', icon: Home, end: true as const };

const baseNavItems = [
  homeNavItem,
  { to: '/assets', label: '素材', icon: Image, end: false as const },
  { to: '/knowledge', label: '知识颗粒', icon: BookOpen, end: false as const },
];

const ttqNavItem = { to: '/ttq', label: '品题', icon: Utensils, end: false as const };

const reviewNavItem = { to: '/review', label: '复习', icon: Sparkles, end: false as const };

function buildPrimaryNavItems(showTtq: boolean, offscreenOnlyHome: boolean) {
  if (offscreenOnlyHome) {
    return [homeNavItem];
  }
  return [
    ...baseNavItems,
    ...(showTtq ? [ttqNavItem] : []),
    reviewNavItem,
  ];
}

function useTtqSubjectRouteGuard() {
  const { activeSubject } = useActiveSubject();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isTtqSubjectEnabled(activeSubject) && location.pathname.startsWith('/ttq')) {
      navigate('/', { replace: true });
    }
  }, [activeSubject, location.pathname, navigate]);
}

/** Off-screen product mode: keep learner on the home player surface. */
function useOffscreenNavGuard() {
  const { productMode } = useProductStudyMode();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (productMode !== 'offscreen') return;
    const path = location.pathname;
    const allowed =
      path === '/'
      || path.startsWith('/assets')
      || path.startsWith('/settings/learning')
      || path.startsWith('/knowledge/')
      || path.startsWith('/about')
      || path.startsWith('/privacy')
      || path.startsWith('/profile')
      || path.startsWith('/download');
    if (!allowed) {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate, productMode]);
}

/** Paths under `/ttq/:segment` that are pages, not session canvases. */
const TTQ_NON_SESSION_SEGMENTS = new Set(['recommendations']);

function isTtqSessionCanvasPath(pathname: string) {
  const normalized = pathname.replace(/\/$/, '');
  const match = /^\/ttq\/([^/]+)$/.exec(normalized);
  if (!match) return false;
  return !TTQ_NON_SESSION_SEGMENTS.has(match[1]);
}

function isTtqWorkbenchPath(pathname: string) {
  return /\/ttqworkbench\//.test(pathname);
}

function hideTtqAppChrome(pathname: string) {
  return isTtqSessionCanvasPath(pathname) || isTtqWorkbenchPath(pathname);
}

function isAssetDetailPath(pathname: string) {
  return /^\/assets\/[^/]+/.test(pathname);
}

function resolveShellWidthClass(pathname: string, feedLayout: boolean) {
  if (feedLayout) return 'max-w-[1280px]';
  if (isAssetDetailPath(pathname)) return 'max-w-none';
  return 'max-w-7xl';
}

function AppLayoutRouteEffects() {
  useTtqSubjectRouteGuard();
  useOffscreenNavGuard();
  return null;
}

function AppLayoutHeader() {
  const { activeSubject, setActiveSubject } = useActiveSubject();
  const { productMode } = useProductStudyMode();
  const location = useLocation();
  const isOffscreen = productMode === 'offscreen';
  const feedLayout = location.pathname === '/'
    || location.pathname === '/assets'
    || location.pathname === '/pas';
  const shellWidthClass = resolveShellWidthClass(location.pathname, feedLayout);
  const showTtqNav = isTtqSubjectEnabled(activeSubject);
  const primaryNavItems = useMemo(
    () => buildPrimaryNavItems(showTtqNav, isOffscreen),
    [isOffscreen, showTtqNav],
  );

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-surface/95 backdrop-blur">
      <div className={`mx-auto flex items-center gap-4 px-4 py-3 ${shellWidthClass}`}>
        <Link
          to="/"
          className="inline-flex shrink-0 items-center gap-2 rounded-lg py-1 pr-1 text-ink transition hover:opacity-90"
          aria-label={`回到${appConfig.appName}首页`}
        >
          <img
            src="/icon_main.png"
            alt=""
            className="h-8 w-8 rounded-lg"
          />
          <span className="hidden text-lg font-semibold sm:inline">{appConfig.appName}</span>
        </Link>

        <div className="inline-flex items-center rounded-lg border border-border bg-canvas/60 px-1.5 py-0.5">
          <SubjectPickerButton
            subject={activeSubject}
            onSubjectChange={setActiveSubject}
            size="sm"
            showLabel
            menuAlign="left"
          />
          <span className="sr-only">当前学科：{SUBJECT_CONFIG[activeSubject].label}</span>
        </div>

        <div className="ml-auto flex items-center gap-1">
          <nav className="hidden items-center gap-1 overflow-x-auto md:flex">
            {primaryNavItems.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${
                    isActive ? 'bg-primary/10 text-primary font-medium' : 'text-muted hover:bg-black/5 hover:text-ink'
                  }`
                }
              >
                <Icon size={16} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
          <AppMenuDrawer />
        </div>
      </div>
    </header>
  );
}

function AppLayoutBottomNav() {
  const { activeSubject } = useActiveSubject();
  const { productMode } = useProductStudyMode();
  const isOffscreen = productMode === 'offscreen';
  const showTtqNav = isTtqSubjectEnabled(activeSubject);
  const primaryNavItems = useMemo(
    () => buildPrimaryNavItems(showTtqNav, isOffscreen),
    [isOffscreen, showTtqNav],
  );
  const colClass = primaryNavItems.length <= 1
    ? 'grid-cols-1'
    : showTtqNav && !isOffscreen
      ? 'grid-cols-5'
      : 'grid-cols-4';

  return (
    <nav className="sticky bottom-0 z-40 border-t border-border bg-surface md:hidden">
      <div className={`grid ${colClass}`}>
        {primaryNavItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-2 text-xs ${isActive ? 'text-primary' : 'text-muted'}`
            }
          >
            <Icon size={20} />
            {label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function AppLayout() {
  const location = useLocation();
  if (peekPreferFactoryLogin() || peekWechatFactoryReturn()) {
    return <Navigate to="/login?intent=factory" replace />;
  }
  const feedLayout = location.pathname === '/'
    || location.pathname === '/assets'
    || location.pathname === '/pas';
  const assetDetailLayout = isAssetDetailPath(location.pathname);
  const shellWidthClass = resolveShellWidthClass(location.pathname, feedLayout);
  const hideAppChrome = hideTtqAppChrome(location.pathname);
  const ttqCanvasOnly = isTtqSessionCanvasPath(location.pathname);

  return (
    <ActiveSubjectProvider>
      <ProductStudyModeProvider>
        <AppLayoutInner
          hideAppChrome={hideAppChrome}
          ttqCanvasOnly={ttqCanvasOnly}
          feedLayout={feedLayout}
          assetDetailLayout={assetDetailLayout}
          shellWidthClass={shellWidthClass}
        />
      </ProductStudyModeProvider>
    </ActiveSubjectProvider>
  );
}

function AppLayoutInner({
  hideAppChrome,
  ttqCanvasOnly,
  feedLayout,
  assetDetailLayout,
  shellWidthClass,
}: {
  hideAppChrome: boolean;
  ttqCanvasOnly: boolean;
  feedLayout: boolean;
  assetDetailLayout: boolean;
  shellWidthClass: string;
}) {
  const location = useLocation();
  const { productMode } = useProductStudyMode();
  const offscreenHome = productMode === 'offscreen' && location.pathname === '/';

  return (
    <>
      <AppLayoutRouteEffects />
      <div className="flex h-dvh flex-col overflow-hidden bg-canvas">
        {!hideAppChrome ? <AppLayoutHeader /> : null}

        <main
          className={
            ttqCanvasOnly
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden p-0'
              : hideAppChrome
                ? 'min-h-0 flex-1 overflow-y-auto p-0'
                : offscreenHome
                  ? 'mx-auto flex w-full max-w-[1280px] min-h-0 flex-1 flex-col overflow-hidden px-2 pb-0 pt-0 sm:px-3'
                  : feedLayout
                    ? 'mx-auto w-full max-w-[1280px] min-h-0 flex-1 overflow-y-auto px-2 pb-4 pt-0 sm:px-3'
                    : assetDetailLayout
                      ? `mx-auto w-full ${shellWidthClass} min-h-0 flex-1 overflow-y-auto px-2 py-4 sm:px-3`
                      : `mx-auto w-full ${shellWidthClass} min-h-0 flex-1 overflow-y-auto px-4 py-6`
          }
        >
          {offscreenHome ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <Outlet />
            </div>
          ) : (
            <Outlet />
          )}
        </main>

        {!hideAppChrome ? <AppLayoutBottomNav /> : null}

        {!hideAppChrome && !offscreenHome ? <IcpFooter /> : null}
      </div>
    </>
  );
}
